import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getFarmOverviewPath } from '../utils/farmRoutes';
import { apiUrl } from '../utils/apiUrl';
import { clearStoredRedirectTarget, getStoredRedirectTarget, setStoredAuth } from '../utils/authStorage';

function getDefaultAuthenticatedPath(user, token) {
  const farmIdFromUser = user?.farmId;
  if (farmIdFromUser) {
    const path = getFarmOverviewPath(farmIdFromUser);
    if (path) return path;
  }
  try {
    const payload = JSON.parse(atob(String(token || '').split('.')[1]));
    const path = getFarmOverviewPath(payload.farmId);
    if (path) return path;
  } catch {
    /* fall through */
  }
  return '/dashboard';
}

export function getSafeRedirectTarget(from, fallback) {
  if (typeof from !== 'string') return fallback;
  if (!from.startsWith('/') || from.startsWith('//')) return fallback;
  if (from === '/login' || from.startsWith('/login?')) return fallback;
  return from;
}

function getRedirectFromLocation(location) {
  const search = new URLSearchParams(location.search || '');
  return location.state?.from || search.get('redirect') || getStoredRedirectTarget();
}

export default function LoginPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, ready, refresh } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirectTarget = useMemo(() => {
    return getSafeRedirectTarget(getRedirectFromLocation(location), getDefaultAuthenticatedPath(user, token));
  }, [location, token, user]);

  useEffect(() => {
    if (!ready || !token) return;
    navigate(redirectTarget, { replace: true });
  }, [navigate, ready, redirectTarget, token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username, password })
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setStoredAuth(data.token, username);
      const nextUser = await refresh();
      if (!nextUser) {
        throw new Error('failed');
      }
      const target = getSafeRedirectTarget(getRedirectFromLocation(location), getDefaultAuthenticatedPath(nextUser, data.token));
      clearStoredRedirectTarget();
      navigate(target, { replace: true });
    } catch (err) {
      setError(t('loginFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-3" style={{ maxWidth: 400 }}>
      <h1 className="mb-3">{t('login')}</h1>
      <form onSubmit={submit}>
        <input
          type="text"
          className="form-control mb-2"
          placeholder={t('username')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          type="password"
          className="form-control mb-2"
          placeholder={t('password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <div className="text-danger mb-2">{error}</div>}
        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {t('login')}
        </button>
      </form>
    </div>
  );
}
